import {
  Flex,
  Button,
  Text,
  Spacer,
  Stack,
  Switch,
  useDisclosure,
  Box,
  useOutsideClick,
  useToast,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Global } from "@emotion/react";
import { LedgerType } from "@syla/shared/types/models/LedgerBase";
import { TransactionType } from "@syla/shared/types/models/TransactionBase";
import { StatusOption } from "@syla/shared/types/requests/GetGroupsRequest";
import { addDays, format, addMinutes, addMilliseconds } from "date-fns";
import { intersection, difference, uniqBy, isArray } from "lodash";
import { RangeValue } from "rc-picker/lib/interface";
import React, {
  Dispatch,
  SetStateAction,
  useRef,
  useState,
  useMemo,
  useEffect,
  useContext,
} from "react";
import { FaCaretDown } from "react-icons/fa";
import { useQuery } from "react-query";

import { getAssetLongName } from "@syla/shared/helpers/assets/getAssetLongName";
import { getAssetShortName } from "@syla/shared/helpers/assets/getAssetShortName";
import { getUserAssets } from "../../../../api/asset/getUserAssets";
import { getUserLedgerTypes } from "../../../../api/transactions/getUserLedgerTypes";
import { ModalContext } from "../../../../contexts/ModalContext";
import { deleteMatchingGroups } from "../../../../store/actions/groupList";
import {
  useGetUserFinancialYearsData,
  setSelectedFinancialYear,
} from "../../../../store/actions/userFinancialYear";
import {
  getInLedgerTypeOptionsByTransactionType,
  getOutLedgerTypeOptionsByTransactionType,
  getFeeLedgerTypeOptionsByTransactionType,
} from "../../../../helper/transaction/getLedgerType";
import { getTransactionTypeOptions } from "../../../../helper/transaction/getTransactionTypeOptions";
import { useGetWalletOptions } from "../../../../hooks/wallet/useGetWalletOptions";
import {
  TRANSACTIONS_CACHE_KEY,
  useTransactionsStore,
} from "../../../../store/transactionsStore";
import { OrderFilterSetting } from "../../../../types/order/orderQuery";
import { ButtonVariant } from "../../../atoms/ButtonVariant";
import { DatePicker, rangePickerStyles } from "../../../atoms/DatePicker";
import { sylaSuggestionsText } from "../../../atoms/NeedsReview";
import { SingleSelectBox } from "../../../atoms/singleSelectBox";
import { MultiThumbnailSelectBox } from "../../../atoms/thumbnailSelectBoxVariant/multiThumbnailSelectBox/MultiThumbnailSelectBox";
import { maxAssetsInView } from "../ledgerInputConstants";

const { RangePicker } = DatePicker;

interface FilterSectionProps {
  filterSetting: OrderFilterSetting;
  setFilterSetting: Dispatch<SetStateAction<OrderFilterSetting>>;
}

export const FilterSection = ({
  filterSetting,
  setFilterSetting,
}: FilterSectionProps): JSX.Element => {
  // state for the financial year filter
  const [financialYear, setFinancialYear] = useState<string | undefined>();

  const {
    isOpen: isDateFilterBtnOpen,
    onToggle: onDateFilterBtnToggle,
    onClose: onDataFilterBtnClose,
  } = useDisclosure({ defaultIsOpen: false });

  // Custom component date filter button ref
  const dateFilterBtnRef = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: dateFilterBtnRef,
    handler: () => onDataFilterBtnClose(),
  });

  const outputDateSelection = () => {
    if (filterSetting.datePeriod?.[0] && filterSetting.datePeriod?.[1]) {
      return `${format(filterSetting.datePeriod[0], "d MMM yyyy")} - ${format(
        filterSetting.datePeriod[1],
        "d MMM yyyy"
      )}`;
    }
    return "DATE";
  };

  const walletOptions = useGetWalletOptions()?.data;

  // query for this user's assets
  const { data: userAssetsData, isLoading: userAssetsIsLoading } = useQuery(
    [TRANSACTIONS_CACHE_KEY, "user-assets-selection-options"],
    async () => {
      const userAssets = await getUserAssets();

      // need to format it to a selection option object
      return userAssets.map((asset) => ({
        value: asset._id,
        label: getAssetShortName(asset),
        icon: asset.image,
        description: getAssetLongName(asset),
      }));
    }
  );

  const { data: userFinancialYearsData } = useGetUserFinancialYearsData();

  // ledger types
  const { data: userLedgerTypes } = useQuery(
    [TRANSACTIONS_CACHE_KEY, "user-ledger-types"],
    getUserLedgerTypes
  );

  const ledgerTypeOptions = useMemo(() => {
    const getTypeOptionsByTransactionType = (transactionType) =>
      getInLedgerTypeOptionsByTransactionType(transactionType)
        .concat(getOutLedgerTypeOptionsByTransactionType(transactionType))
        .concat(getFeeLedgerTypeOptionsByTransactionType(transactionType))
        .filter((lt) => !userLedgerTypes || userLedgerTypes.includes(lt.value));

    // get types matching transaction types selected
    // or get all types otherwise
    const types = filterSetting.transactionType.length
      ? filterSetting.transactionType.flatMap((transactionType) =>
          getTypeOptionsByTransactionType(transactionType)
        )
      : Object.values(TransactionType).flatMap((transactionType) =>
          getTypeOptionsByTransactionType(transactionType)
        );

    return uniqBy(types, (type) => type.value);
  }, [filterSetting.transactionType, userLedgerTypes]);

  useEffect(() => {
    const validLedgerTypes = ledgerTypeOptions.map((lt) => lt.value);

    if (
      filterSetting.ledgerType.length &&
      difference(filterSetting.ledgerType, validLedgerTypes).length
    )
      setFilterSetting({
        ...filterSetting,
        ledgerType: intersection(filterSetting.ledgerType, validLedgerTypes),
      });
  }, [filterSetting, ledgerTypeOptions, setFilterSetting]);

  const filtersActive = useMemo(() => {
    const appliedFilters = Object.values(filterSetting).filter((filterValue) =>
      isArray(filterValue) ? filterValue.length : filterValue
    );
    return !!appliedFilters.length;
  }, [filterSetting]);

  const { openModal, setBtn2Loading, closeModal } = useContext(ModalContext);
  const { groupListTotal } = useTransactionsStore();
  const toast = useToast();

  // mobile filter by toggle
  const [showFilters, setShowFilters] = useState(false);
  const showFilterSwitch = useBreakpointValue({
    base: true,
    sm: true,
    md: false,
    xs: true,
    "2xs": true,
  });

  return (
    <Stack
      direction={showFilterSwitch ? "column" : "row"}
      w="100%"
      alignItems={showFilterSwitch ? "left" : "center"}
      p="0 15px 15px 15px"
      bgColor="white.0"
      wrap="wrap"
      gridGap="5px"
    >
      <Stack direction="row" justifyContent="space-between">
        <Text
          textTransform="uppercase"
          mr="20px"
          color="black.550"
          fontSize="0.875rem"
          whiteSpace="nowrap"
        >
          Filter by:
        </Text>
        {showFilterSwitch && (
          <Switch
            size="md"
            colorScheme="red"
            onChange={() => setShowFilters(!showFilters)}
            padding="2px 5px 0px 10px"
          />
        )}
      </Stack>
      {(!showFilterSwitch || showFilters) && (
        <>
          {/* -------------------------- Action ---------------------- */}
          <MultiThumbnailSelectBox
            selectedOptions={filterSetting.transactionType}
            onChangeSelection={(selection) =>
              setFilterSetting((oldState) => ({
                ...oldState,
                transactionType: selection as TransactionType[],
              }))
            }
            options={getTransactionTypeOptions}
            placeholder="EVENT"
            mr="10px"
            selectBtnProps={{ height: "30px" }}
            optionContainerProps={{ width: "200px", top: "30px" }}
          />
          {/* -------------------------- TYPE ---------------------- */}
          <MultiThumbnailSelectBox
            selectedOptions={filterSetting.ledgerType}
            onChangeSelection={(selection) =>
              setFilterSetting((oldState) => ({
                ...oldState,
                ledgerType: selection as LedgerType[],
              }))
            }
            options={ledgerTypeOptions}
            maxOptionsInView={10}
            searchEnable
            placeholder="TYPE"
            mr="10px"
            selectBtnProps={{ height: "30px" }}
            optionContainerProps={{ width: "300px", top: "30px" }}
          />
          {/* -------------------------- Data Source ---------------------- */}
          <MultiThumbnailSelectBox
            selectedOptions={filterSetting.dataSource}
            onChangeSelection={(selection) =>
              setFilterSetting((oldState) => ({
                ...oldState,
                dataSource: selection,
              }))
            }
            options={walletOptions || []}
            maxOptionsInView={5}
            placeholder="DATA SOURCE"
            mr="10px"
            selectBtnProps={{ height: "30px" }}
            optionContainerProps={{ width: "250px", top: "30px" }}
          />
          {/* -------------------------- Asset ---------------------- */}
          <MultiThumbnailSelectBox
            doubleRows
            selectedOptions={filterSetting.asset}
            onChangeSelection={(selection) =>
              setFilterSetting((oldState) => ({
                ...oldState,
                asset: selection,
              }))
            }
            options={userAssetsData ?? []}
            maxOptionsInView={maxAssetsInView}
            searchEnable
            placeholder="ASSET"
            mr="10px"
            mb="8px"
            selectBtnProps={{ height: "30px" }}
            optionContainerProps={{ width: "450px", top: "30px" }}
          />
          {/* -------------------------- Status ---------------------- */}
          <MultiThumbnailSelectBox
            showIcon={false}
            selectedOptions={filterSetting.status}
            onChangeSelection={(selection: any) =>
              setFilterSetting((oldState) => ({
                ...oldState,
                status: selection,
              }))
            }
            options={[
              {
                label: sylaSuggestionsText,
                value: StatusOption.NeedsReview,
              },
            ]}
            placeholder="STATUS"
            selectBtnProps={{ height: "30px" }}
            optionContainerProps={{ width: "150px", top: "40px" }}
          />

          {/* -------------------------- Date ---------------------- */}

          <Box position="relative" ref={dateFilterBtnRef} ml="10px">
            <Button
              h="30px"
              bgColor={
                filterSetting.datePeriod !== null ? "white.500" : "white.0"
              }
              borderColor={
                filterSetting.datePeriod !== null ? "black.1000" : "white.500"
              }
              borderWidth="1px"
              _hover={{
                bgColor: "white.500",
              }}
              _active={{
                bgColor: "black.200",
              }}
              onClick={() => onDateFilterBtnToggle()}
              w="100%"
            >
              <Stack direction="row" justifyContent="space-between" w="100%">
                <Text
                  fontWeight="500"
                  fontSize="0.875rem"
                  color="black.550"
                  mr="10px"
                >
                  {outputDateSelection()}
                </Text>
                <FaCaretDown color="#808080" />
              </Stack>
            </Button>
            {isDateFilterBtnOpen && (
              <Flex
                position="absolute"
                zIndex={2}
                bgColor="white.0"
                w={showFilterSwitch ? "100%" : "500px"}
                direction="column"
                borderWidth="1px"
                borderColor="white.500"
                top="40px"
                left={showFilterSwitch ? "0px" : "-400px"}
                borderRadius="5px"
                boxShadow=" 0px 4px 30px rgba(0, 0, 0, 0.08)"
                py="5px"
              >
                <Flex
                  h="45px"
                  alignItems="center"
                  px="15px"
                  justifyContent="space-between"
                >
                  <Text color="black.700" fontSize="0.875rem">
                    Financial Year
                  </Text>
                  <SingleSelectBox
                    allowUnSelect
                    options={userFinancialYearsData ?? []}
                    selectedOption={financialYear}
                    onChangeSelection={(selection) => {
                      setFinancialYear(selection);
                      setSelectedFinancialYear(
                        userFinancialYearsData?.find(
                          (financialYear) => financialYear.value === selection
                        )
                      );
                      if (!selection || !userFinancialYearsData) {
                        setFilterSetting((oldState) => ({
                          ...oldState,
                          datePeriod: null,
                        }));
                      } else {
                        // find the selected financial year
                        const selectedFinancialYear =
                          userFinancialYearsData.find(
                            (year) => year.value === selection
                          );

                        // set to null and early return if no financial year was found for the selection
                        if (!selectedFinancialYear) {
                          setFilterSetting((oldState) => ({
                            ...oldState,
                            datePeriod: null,
                          }));
                          return;
                        }

                        setFilterSetting((oldState) => ({
                          ...oldState,
                          datePeriod: selectedDateRangeToFilterDateRange([
                            selectedFinancialYear.startDate,
                            selectedFinancialYear.endDate,
                          ]),
                        }));
                      }
                    }}
                    placeholder="Select a financial year..."
                    w="255px"
                    selectBtnProps={{
                      height: "30px",
                      borderColor: "white.500",
                    }}
                    optionContainerProps={{ top: "40px", color: "#4d4d4d" }}
                    selectBtnTextProps={{ fontWeight: "normal" }}
                  />
                </Flex>
                <Flex
                  h="45px"
                  alignItems="center"
                  px="15px"
                  justifyContent="space-between"
                >
                  <Text color="black.700" fontSize="0.875rem">
                    Date Range
                  </Text>
                  <div id="datePickerDiv">
                    <Global styles={rangePickerStyles} />
                    <RangePicker
                      value={filterSetting.datePeriod}
                      onChange={(date) => {
                        // ant date picker's issue
                        // first time it will use current date time of hours/minutes
                        if (date) {
                          date[0]?.setUTCHours(0, 0, 0, 0);
                          date[1]?.setUTCHours(0, 0, 0, 0);
                        }

                        setFinancialYear(undefined);
                        setFilterSetting((oldState) => ({
                          ...oldState,
                          datePeriod: date,
                        }));
                      }}
                      format="dd MMM yyyy"
                      getPopupContainer={() =>
                        document.getElementById("datePickerDiv") as HTMLElement
                      }
                      suffixIcon
                      style={{
                        borderRadius: "5px",
                        borderColor: "#f5f5f5 !important",
                      }}
                    />
                  </div>
                </Flex>
              </Flex>
            )}
          </Box>
        </>
      )}
      {/* -------------------------- Reset Button ---------------------- */}
      <Spacer />
      {filtersActive && !!groupListTotal && (
        <ButtonVariant
          content="Delete all.."
          outlineType="outlineRed"
          onClick={() =>
            openModal({
              type: "confirmation",
              icon: "delete",
              heading: `Are you sure you want to delete all ${groupListTotal} transactions matching these filters?`,
              contents: [
                "This will delete matching transactions across all pages. " +
                  "You will have to re-import them again if you require them later.",
              ],
              btn2Content: "Delete",
              btn2Action: async () => {
                setBtn2Loading(true);
                try {
                  await deleteMatchingGroups();
                  closeModal();
                } catch (error: any) {
                  toast({
                    title: "Error deleting groups",
                    description: error?.response?.data || "Unexpected error.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                  });
                  setBtn2Loading(false);
                  closeModal();
                }
              },
            })
          }
          height="28px"
          fontSize="0.75rem"
          borderWidth="1px !important"
        />
      )}
      {filtersActive && (
        <ButtonVariant
          content="Clear Filters"
          outlineType="ghost"
          onClick={() => {
            setFilterSetting((oldState) => ({
              ...oldState,
              transactionType: [],
              ledgerType: [],
              dataSource: [],
              asset: [],
              status: [],
              datePeriod: null,
              financialYear: undefined,
              searchQuery: "",
            }));
            setFinancialYear(undefined);
          }}
          fontSize="0.875rem"
          h="28px"
          fontWeight="normal"
        />
      )}
    </Stack>
  );
};

/** Convert api range with exclusive end date to datepicker range */
const selectedDateRangeToFilterDateRange = (
  dateRange: RangeValue<Date>
): RangeValue<Date> =>
  dateRange && dateRange[0] && dateRange[1]
    ? [
        shiftTimeFromBrisbane(dateRange[0]),
        shiftTimeFromBrisbane(addDays(addMilliseconds(dateRange[1], 1), -1)),
      ]
    : null;

const shiftTimeFromBrisbane = (date: Date): Date => addMinutes(date, 600); // +10:00 is 600 minutes
